import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { tarifService } from '@services/api'

const AdminTarifPage = () => {
  // Vérification des permissions dès le début
  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')
      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }

  const userRoles = getCurrentUserRoles()
  const hasAccess = userRoles.length === 0 || userRoles.some(r =>
    ['super admin', 'admin', 'superviseur'].includes(r.toLowerCase())
  )

  const [loading, setLoading] = useState(false)
  const [tarifs, setTarifs] = useState({
    articles: [],
    videos: [],
    banners: []
  })

  useEffect(() => {
    if (hasAccess) {
      fetchTarifs()
    }
  }, [hasAccess])

  const mapTarif = (t) => ({
    id: t.IdTarifs,
    price: t.montant_Tarifs,
    days: t.nb_jours,
    imageCount: t.nb_images,
    label: t.libelle_Tarifs,
    code: t.code_Tarifs,
    package: t.indification_package,
  })

  const fetchTarifs = async () => {
    try {
      const result = await tarifService.getTarifs()
      const data = result.data || {}
      setTarifs({
        articles: [
          ...(data.I3 || []).map(mapTarif),
          ...(data.I6 || []).map(mapTarif),
          ...(data.I9 || []).map(mapTarif),
        ],
        videos: (data.V || []).map(mapTarif),
        banners: (data.B || []).map(mapTarif),
      })
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error)
      toast.error('Erreur lors du chargement des tarifs')
    }
  }

  const handleUpdateTarif = (type, tarifId, newPrice) => {
    setTarifs(prev => ({
      ...prev,
      [type]: prev[type].map(tarif =>
        tarif.id === tarifId ? { ...tarif, price: newPrice } : tarif
      )
    }))
  }

  const handleSaveTarifs = async () => {
    setLoading(true)
    try {
      const allTarifs = [
        ...tarifs.articles,
        ...tarifs.videos,
        ...tarifs.banners,
      ]
      await Promise.all(allTarifs.map(tarif =>
        tarifService.updateTarif(tarif.id, {
          montant_Tarifs: tarif.price,
          nb_jours: tarif.days,
          nb_images: tarif.imageCount,
          libelle_Tarifs: tarif.label,
          code_Tarifs: tarif.code,
          indification_package: tarif.package,
        })
      ))
      toast.success('Tarifs mis à jour avec succès !')
      fetchTarifs()
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  // Si pas d'accès, afficher message d'erreur
  if (!hasAccess) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
            <p className="text-sm text-gray-500 mt-2">Rôles autorisés : Super Admin, Admin, Superviseur</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-white rounded-md p-6 mb-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-md flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Gestion des Tarifs</h1>
              <p className="text-gray-600 mt-1">Prix des publications par type et durée</p>
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="space-y-6">
          {/* Tarifs Articles */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-md p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center text-white text-xl">
                📄
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Articles</h4>
                <p className="text-sm text-gray-600">Tarifs par durée et nombre d'images</p>
              </div>
            </div>
            {tarifs.articles.length > 0 ? (
              <div className="space-y-3">
                {/* Grouper par nombre d'images */}
                {[3, 6, 9].map(imageCount => {
                  const articlesByImages = tarifs.articles.filter(t => t.imageCount === imageCount)
                  if (articlesByImages.length === 0) return null
                  return (
                    <div key={imageCount} className="bg-white rounded-md p-4 border border-slate-200">
                      <p className="text-xs font-semibold text-gray-700 mb-3">{imageCount} images</p>
                      <div className="grid grid-cols-4 gap-2">
                        {articlesByImages.map((tarif) => (
                          <div key={tarif.id} className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                            <div className="flex items-center gap-1">
                              <input 
                                type="number" 
                                value={tarif.price} 
                                onChange={(e) => handleUpdateTarif('articles', tarif.id, parseFloat(e.target.value) || 0)}
                                className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-amber-200" 
                              />
                              <span className="text-xs text-gray-600 whitespace-nowrap">F</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                Chargement des tarifs...
              </div>
            )}
          </div>

          {/* Tarifs Vidéos */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-md p-6 border border-purple-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500 rounded-md flex items-center justify-center text-white text-xl">
                🎥
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Vidéos</h4>
                <p className="text-sm text-gray-600">Tarifs par durée de publication</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {tarifs.videos.length > 0 ? (
                tarifs.videos.map((tarif) => (
                  <div key={tarif.id} className="bg-white rounded-md p-3 border border-slate-200">
                    <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        value={tarif.price} 
                        onChange={(e) => handleUpdateTarif('videos', tarif.id, parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-amber-200" 
                      />
                      <span className="text-xs text-gray-600 whitespace-nowrap">FCFA</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-4 text-center py-4 text-gray-500 text-sm">
                  Chargement des tarifs...
                </div>
              )}
            </div>
          </div>

          {/* Tarifs Bannières */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-md p-6 border border-orange-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center text-white text-xl">
                📢
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Bannières</h4>
                <p className="text-sm text-gray-600">Tarifs par durée de publication</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {tarifs.banners.length > 0 ? (
                tarifs.banners.map((tarif) => (
                  <div key={tarif.id} className="bg-white rounded-md p-3 border border-slate-200">
                    <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        value={tarif.price} 
                        onChange={(e) => handleUpdateTarif('banners', tarif.id, parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-amber-200" 
                      />
                      <span className="text-xs text-gray-600 whitespace-nowrap">FCFA</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-4 text-center py-4 text-gray-500 text-sm">
                  Chargement des tarifs...
                </div>
              )}
            </div>
          </div>

          {/* Bouton sauvegarder les tarifs */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveTarifs}
              disabled={loading}
              className="px-6 py-3 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Enregistrer les tarifs
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminTarifPage
